// components
import Button from '@mui/material/Button';
import { Dialog, EmailField, showSnackbar } from 'components/shared';
import type { EmploymentAgency, SelfMatchAction } from 'components/types';
import { StyledFlexMatchSettingsDialogActions } from './FlexMatchSettingsDialog.styles';
import InfoCell from './InfoCell';
import CheckboxTable from 'components/shared/self-match/CheckboxTable';
import MatchActionSelector from 'components/shared/self-match/MatchActionSelector';
import ShowWarning from './ShowWarning';

// enums
import {
  EmployeeCostCenterType,
  SelfMatchAction as SelfMatchActionEnum
} from 'components/enums';

// hooks
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

// types
import type { EmployeeCostCenterAction } from '../types';
import type { DialogProps, ExternalMatchActions } from './types';

// utils
import classNames from 'classnames';
import { addSeconds, max, min } from 'date-fns';
import { entries, sortBy, values } from 'lodash';
import { setUserPreference } from 'redux/actionCreators';
import { getFlexMatchSettingAction } from './utils';
import { fromISOString } from 'utils/date';
import { sortAlphabeticallyBy } from 'utils/misc';

// queries & mutations
import { getEmploymentAgencies } from 'RestAPIs/cost-centers';
import { saveMatchSettings } from 'RestAPIs/roster/requests';
import { FlexMatchSettingsDialogStyles } from './FlexMatchSettingsDialogStyles';

export default function FlexMatchSettingsDialog({
  open = true,
  requests,
  showRegionalPools,
  showAgencies,
  onSettingsSaved,
  onHidden,
  singleRowSettings = false
}: DialogProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { flexAgencyResponseEmail } = useSelector((state) => state.preferences);
  const [hide, setHide] = useState(false);
  const [preference1, setPreference1] = useState<SelfMatchAction | null>(
    getFlexMatchSettingAction({
      requests,
      employeeCostCenterType: EmployeeCostCenterType.PREFERENCE1
    })
  );
  const [pref1DeployableAsFlex, setPref1DeployableAsFlex] =
    useState<SelfMatchAction | null>(
      getFlexMatchSettingAction({
        requests,
        employeeCostCenterType:
          EmployeeCostCenterType.DEPLOYABLE_AS_FLEX_PREFERENCE1
      })
    );
  const [flex, setFlex] = useState<SelfMatchAction | null>(
    getFlexMatchSettingAction({
      requests,
      employeeCostCenterType: EmployeeCostCenterType.FLEX
    })
  );
  const [deployableAsFlex, setDeployableAsFlex] =
    useState<SelfMatchAction | null>(
      getFlexMatchSettingAction({
        requests,
        employeeCostCenterType: EmployeeCostCenterType.DEPLOYABLE_AS_FLEX
      })
    );
  const [regionalPools, setRegionalPools] = useState<EmploymentAgency[]>([]);
  const [regionalPoolActions, setRegionalPoolActions] =
    useState<ExternalMatchActions>({});
  const [agencies, setAgencies] = useState<EmploymentAgency[]>([]);
  const [agencyActions, setAgencyActions] = useState<ExternalMatchActions>({});

  const [showEmailField, setShowEmailField] = useState(false);
  const [emailAddress, setEmailAddress] = useState(
    flexAgencyResponseEmail || ''
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    setHide(false);
    onHidden?.();
  };

  const addExternalSettings = (
    actions: ExternalMatchActions,
    employeeCostCenterActions: EmployeeCostCenterAction[]
  ) => {
    const settings = entries(actions)
      .filter((arr) => !!arr[1])
      .map(([id, action]) => ({
        employeeCostCenterType:
          EmployeeCostCenterType.EXTERNAL as EmployeeCostCenterType.EXTERNAL,
        costCenterId: parseInt(id, 10),
        action: action as SelfMatchAction
      }));
    employeeCostCenterActions.push(...settings);
  };

  const saveSettings = () => {
    setIsSaving(true);
    const employeeCostCenterActions: EmployeeCostCenterAction[] = [];
    if (preference1) {
      employeeCostCenterActions.push({
        employeeCostCenterType: EmployeeCostCenterType.PREFERENCE1,
        action: preference1
      });
    }
    if (pref1DeployableAsFlex) {
      employeeCostCenterActions.push({
        employeeCostCenterType:
          EmployeeCostCenterType.DEPLOYABLE_AS_FLEX_PREFERENCE1,
        action: pref1DeployableAsFlex
      });
    }
    if (flex) {
      employeeCostCenterActions.push({
        employeeCostCenterType: EmployeeCostCenterType.FLEX,
        action: flex
      });
    }
    if (deployableAsFlex) {
      employeeCostCenterActions.push({
        employeeCostCenterType: EmployeeCostCenterType.DEPLOYABLE_AS_FLEX,
        action: deployableAsFlex
      });
    }
    if (showRegionalPools) {
      addExternalSettings(regionalPoolActions, employeeCostCenterActions);
    }

    if (showAgencies) {
      addExternalSettings(agencyActions, employeeCostCenterActions);
    }

    saveMatchSettings({
      requestIds: requests.map((r) => r.requestId),
      employeeCostCenterActions,
      emailAddressToRespond: (showEmailField && emailAddress) || null
    }).then(
      () => {
        if (emailAddress !== flexAgencyResponseEmail) {
          dispatch(
            setUserPreference({
              key: 'flexAgencyResponseEmail',
              value: emailAddress
            })
          );
        }
        showSnackbar({ message: t('changesSaved'), severity: 'success' });
        handleClose();
        onSettingsSaved();
      },
      () => {
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
        setIsSaving(false);
      }
    );
  };

  const getActions = (externalList: EmploymentAgency[]) => {
    return externalList.reduce((obj, { id, privilegedEmailAddress, type }) => {
      if (privilegedEmailAddress || type == 'rgp') {
        obj[id] = getFlexMatchSettingAction({
          requests,
          employeeCostCenterType: EmployeeCostCenterType.EXTERNAL,
          costCenterId: id
        });
      } else {
        obj[id] = SelfMatchActionEnum.NONE;
      }
      return obj;
    }, {});
  };

  useEffect(() => {
    if ((showAgencies || showRegionalPools) && requests.length) {
      const dates = requests.map((r) => fromISOString(r.date));
      getEmploymentAgencies({
        startDate: min(dates),
        endDate: addSeconds(max(dates), 1)
      }).then(({ data: externalList }) => {
        const regionalPools = externalList.filter(
          ({ type }) => showRegionalPools && type === 'rgp'
        );
        setRegionalPools(regionalPools);
        setRegionalPoolActions(getActions(regionalPools));

        const agencies = externalList.filter(
          ({ type }) => showAgencies && type === 'agn'
        );
        setAgencies(agencies);
        setAgencyActions(getActions(agencies));

        setShowEmailField(false);
      });
    }
  }, [showAgencies, showRegionalPools, requests]);

  const hasAgencies = agencies.length > 0;
  const hasRegionalPools = regionalPools.length > 0;
  const pref1Actions = [preference1, pref1DeployableAsFlex];
  const allActions = [flex, deployableAsFlex];
  const allPref1None = pref1Actions.every(
    (v) => v === SelfMatchActionEnum.NONE
  );
  const allPref1Subscribe = pref1Actions.every(
    (v) => v === SelfMatchActionEnum.SUBSCRIBE
  );
  const allPref1Connect = pref1Actions.every(
    (v) => v === SelfMatchActionEnum.CONNECT
  );
  const allNone = allActions.every((v) => v === SelfMatchActionEnum.NONE);
  const allSubscribe = allActions.every(
    (v) => v === SelfMatchActionEnum.SUBSCRIBE
  );
  const allConnect = allActions.every((v) => v === SelfMatchActionEnum.CONNECT);
  const allAgenciesNone = values(agencyActions).every(
    (v) => v === SelfMatchActionEnum.NONE
  );
  const allAgenciesSubscribe = values(agencyActions).every(
    (v) => v === SelfMatchActionEnum.SUBSCRIBE
  );
  const allAgenciesConnect = values(agencyActions).every(
    (v) => v === SelfMatchActionEnum.CONNECT
  );
  const allRegionalPoolsNone = values(regionalPoolActions).every(
    (v) => v === SelfMatchActionEnum.NONE
  );
  const allRegionalPoolsSubscribe = values(regionalPoolActions).every(
    (v) => v === SelfMatchActionEnum.SUBSCRIBE
  );
  const allRegionalPoolsConnect = values(regionalPoolActions).every(
    (v) => v === SelfMatchActionEnum.CONNECT
  );

  const checkAllPref1To = (action: SelfMatchAction) => {
    setPreference1(action);
    setPref1DeployableAsFlex(action);
  };

  const checkAllTo = (action: SelfMatchAction) => {
    setFlex(action);
    setDeployableAsFlex(action);
  };

  const checkAllAgenciesTo = (action: SelfMatchAction) => {
    const actions: ExternalMatchActions = {};
    agencies.forEach(({ id, privilegedEmailAddress }) => {
      if (privilegedEmailAddress) {
        actions[id] = action;
      } else {
        actions[id] = SelfMatchActionEnum.NONE;
      }
    });
    setAgencyActions(actions);
    setShowEmailField(action !== 'NONE');
  };

  const checkAllRegionalPoolsTo = (action: SelfMatchAction) => {
    const actions: ExternalMatchActions = {};
    regionalPools.forEach(({ id }) => {
      if (action != SelfMatchActionEnum.SUBSCRIBE) {
        actions[id] = action;
      } else {
        actions[id] = SelfMatchActionEnum.NONE;
      }
    });
    setRegionalPoolActions(actions);
  };

  if (!requests) return null;

  return (
    <Dialog
      title={
        singleRowSettings
          ? t('flexMatchSettingsDialog.title', {
              costCenter: requests[0]?.costCenterNameShort || ''
            })
          : t('flexMatchSettings')
      }
      handleClose={handleClose}
      slotProps={{
        transition: { onExited: () => onHidden() },
        paper: { sx: { width: '480px' } }
      }}
      open={hide ? false : open}
      maxWidth="extralarge"
      scroll="paper"
      onHidden={handleClose}
      actions={
        <StyledFlexMatchSettingsDialogActions>
          <Button
            onClick={saveSettings}
            variant="contained"
            disabled={isSaving || (showEmailField && !emailAddress)}
          >
            {t('save')}
          </Button>
        </StyledFlexMatchSettingsDialogActions>
      }
    >
      <FlexMatchSettingsDialogStyles
        className={classNames({
          'has-agencies': hasAgencies || hasRegionalPools
        })}
      >
        <h3>{t('planUnitPreference1')}</h3>
        <CheckboxTable
          checks={[allPref1None, allPref1Subscribe, allPref1Connect]}
          onChecked={checkAllPref1To}
        />
        <table className="preference-planunit-settings">
          <tbody>
            <tr>
              <td>
                {!preference1 && <ShowWarning />}
                {`${t('flex')}:`}
              </td>
              <MatchActionSelector
                value={preference1}
                onClick={setPreference1}
              />
              <InfoCell message={t('flexMatchSettingsDialog.pref1Info')} />
            </tr>
            <tr>
              <td>
                {!pref1DeployableAsFlex && <ShowWarning />}
                {`${t('deployableAsFlex')}:`}
              </td>
              <MatchActionSelector
                value={pref1DeployableAsFlex}
                onClick={setPref1DeployableAsFlex}
              />
              <InfoCell
                message={t('flexMatchSettingsDialog.pref1DeployableAsFlexInfo')}
              />
            </tr>
          </tbody>
        </table>
        <h3>{t('allPlanUnits')}</h3>
        <CheckboxTable
          checks={[allNone, allSubscribe, allConnect]}
          onChecked={checkAllTo}
        />
        <table className="all-planunit-settings">
          <tbody>
            <tr>
              <td>
                {!flex && <ShowWarning />}
                {`${t('flex')}:`}
              </td>
              <MatchActionSelector value={flex} onClick={setFlex} />
              <InfoCell message={t('flexMatchSettingsDialog.flexInfo')} />
            </tr>
            <tr>
              <td>
                {!deployableAsFlex && <ShowWarning />}
                {`${t('deployableAsFlex')}:`}
              </td>
              <MatchActionSelector
                value={deployableAsFlex}
                onClick={setDeployableAsFlex}
              />
              <InfoCell
                message={t('flexMatchSettingsDialog.deployableAsFlexInfo')}
              />
            </tr>
          </tbody>
        </table>
        {hasRegionalPools && (
          <div className="external-settings">
            <h3>{t('regionalPools')}</h3>
            <CheckboxTable
              checks={[
                allRegionalPoolsNone,
                allRegionalPoolsSubscribe,
                allRegionalPoolsConnect
              ]}
              onChecked={checkAllRegionalPoolsTo}
            />
            <div className="table-wrapper">
              <table>
                <tbody>
                  {sortBy(sortAlphabeticallyBy(regionalPools, 'name'), [
                    (o) => Number(o.sortOrderAgency) || null
                  ]).map(({ id, name }) => (
                    <tr key={id}>
                      <td>
                        {!regionalPoolActions[id] && <ShowWarning />}
                        {name}
                      </td>
                      <MatchActionSelector
                        disabledSubscribe={true}
                        value={regionalPoolActions[id]}
                        onClick={(action) => {
                          const newActions = {
                            ...regionalPoolActions,
                            [id]: action
                          };
                          setRegionalPoolActions(newActions);
                        }}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {hasAgencies && (
          <div className="external-settings">
            <h3>{t('external')}</h3>
            <CheckboxTable
              checks={[
                allAgenciesNone,
                allAgenciesSubscribe,
                allAgenciesConnect
              ]}
              onChecked={checkAllAgenciesTo}
            />
            <table>
              <tbody>
                {sortBy(sortAlphabeticallyBy(agencies, 'name'), [
                  (o) => Number(o.sortOrderAgency) || null
                ]).map(({ id, name, privilegedEmailAddress }) => (
                  <tr key={id}>
                    <td>
                      {!agencyActions[id] && <ShowWarning />}
                      {name}
                    </td>
                    <MatchActionSelector
                      disabled={!privilegedEmailAddress}
                      value={agencyActions[id]}
                      onClick={(action) => {
                        const newActions = {
                          ...agencyActions,
                          [id]: action
                        };
                        setAgencyActions(newActions);
                        setShowEmailField(
                          !values(newActions).every((a) => a === 'NONE')
                        );
                      }}
                    />
                    {!privilegedEmailAddress && (
                      <InfoCell
                        warning
                        message={t(
                          'flexMatchSettingsDialog.noEmailAddressAssociatedWithFlexAgency'
                        )}
                      />
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {showEmailField && (
          <EmailField
            value={emailAddress}
            setEmailValue={(value) => setEmailAddress(value)}
          />
        )}
      </FlexMatchSettingsDialogStyles>
    </Dialog>
  );
}
